import { MemoryBlock } from "./MemoryBlock.js";

const DEFAULT_ALIGNMENT = 1;

export function alignForward(offset, alignment) {
  return Math.ceil(offset / alignment) * alignment;
}

/**
 * FreeList
 *
 * Manages a list of free memory blocks for efficient memory allocation.
 * Provides methods to allocate and deallocate memory blocks.
 */
export class FreeList {
  /**
   * The total capacity of the memory in bytes.
   * @type {number}
   */
  #capacity;

  /**
   * The amount of free memory in bytes.
   * @type {number}
   */
  #freeSpace;

  /**
   * The list of free memory blocks.
   * @type {MemoryBlock[]}
   */
  #blocks;

  /**
   * The buffer that contains this free list.
   * @type {WebGLBuffer|GPUBuffer}
   */
  #buffer;

  /**
   * Creates an instance of FreeList.
   *
   * @param {number} capacity - The total capacity of the memory pool in bytes.
   * @param {WebGLBuffer|GPUBuffer} buffer - The buffer that contains this free list.
   */
  constructor(capacity, buffer) {
    this.#capacity = capacity;
    this.#freeSpace = capacity;
    this.#buffer = buffer;
    this.#freeSpace = this.#capacity;

    // Initialize the free list with a single block of the full capacity
    this.#blocks = [new MemoryBlock(this.#capacity)];
  }

  /**
   * Returns the buffer associated with this free list.
   * @returns {WebGLBuffer|GPUBuffer} The buffer associated with this free list.
   */
  getBuffer() {
    return this.#buffer;
  }

  /**
   * Clears the free list, resetting it to its initial state.
   */
  clear() {
    this.#blocks = [];
    this.#freeSpace = this.#capacity;
    this.#blocks.push(new MemoryBlock(this.#capacity));
  }

  /**
   * Gets the amount of free space in the free list.
   * @returns {number} The amount of free space in bytes.
   */
  getFreeSpace() {
    return this.#freeSpace;
  }

  /**
   * Gets the total capacity of the free list.
   * @returns {number} The total capacity in bytes.
   */
  getCapacity() {
    return this.#capacity;
  }

  /**
   * Gets the head MemoryBlock of the free list chain.
   *
   * @returns {MemoryBlock|null} The head MemoryBlock, or null if the free list is empty.
   */
  getFirstMemoryBlock() {
    if (this.#blocks.length === 0) {
      return null;
    }
    let current = this.#blocks[0];
    while (current.prev) {
      current = current.prev;
    }
    return current;
  }


  /**
   * Allocates a block of memory of the specified size.
   *
   * @param {number} size - The size of the memory block in bytes.
   * @param {number} alignment - The alignment of the memory block in bytes.
   * @returns {MemoryBlock|null} The allocated memory block, or null if no suitable block is found.
   */
  allocate(size, alignment = DEFAULT_ALIGNMENT) {
    // Make sure alignment is never 0 or less then 0
    if (alignment <= 0) {
      alignment = DEFAULT_ALIGNMENT;
    }

    let block = this.#findBestFit(size, alignment);

    if (!block) {
      return null;
    }

    // Calculate the aligned offset of the new block
    const alignedOffset = alignForward(block.offset, alignment);

    // Check if we ended up at the same offset as the original block
    if (alignedOffset !== block.offset) {
      this.#removeBlock(block);
      const blockSizeAfterSplit = alignedOffset - block.offset;
      const nextBlock = this.#splitBlock(block, blockSizeAfterSplit);
      this.#addBlock(block);
      block = nextBlock;
    }

    if (block.offset % alignment !== 0) {
      throw new Error('Block offset is not aligned');
    }

    block.buffer = this.#buffer;
    block.freeList = this;

    if (block.size > size) {
      this.#removeBlock(block);
      const nextBlock = this.#splitBlock(block, size);
      this.#addBlock(nextBlock);
      block.isFree = false;
      return block;
    } else if (block.size === size) {
      this.#removeBlock(block);
      return block;
    }
    // No suitable block found
    return null;
  }

  /**
   * Deallocates a previously allocated memory block.
   *
   * This method adds the block back to the free list and attempts to merge it with adjacent free blocks.
   * If the block is already free, it does nothing.
   */
  free(block) {
    // Check if the block is already free
    if (!block || block.isFree) {
      return;
    }

    this.#addBlock(block);
  }

  /**
   * Checks if there is enough free memory to allocate a block of the specified size.
   *
   * This method searches the free list for a block that can accommodate the requested size.
   * If a suitable block is found, it returns true; otherwise, it returns false.
   *
   * @param {number} size - The size of the memory block in bytes.
   * @returns {boolean} True if a block of the requested size can be allocated, false otherwise.
   */
  canAllocate(size, alignment = DEFAULT_ALIGNMENT) {
    const block = this.#findBestFit(size, alignment);
    return block !== null;
  }

  /**
   * Adds a memory block to the free list and merges it with adjacent free blocks if possible.
   *
   * This method attempts to merge the given block with its previous and next neighbors
   * if they are also free, to reduce fragmentation. The resulting (possibly merged) block
   * is then inserted into the free list in sorted order.
   *
   * @param {MemoryBlock} block - The memory block to add and potentially merge.
   */
  #addBlock(block) {
    // Merge with the previous block if it's free
    if (block.prev && block.prev.isFree) {
      // In order to still be sorted, we need to remove the previous block from the free list
      // before merging it with the current block.
      this.#removeBlock(block.prev);
      block = this.#mergeAdjacentBlocks(block.prev, block);
    }

    // Merge with the next block if it's free
    if (block.next && block.next.isFree) {
      // Same as above, we need to remove the next block from the free list
      // before merging it with the current block.
      this.#removeBlock(block.next);
      block = this.#mergeAdjacentBlocks(block, block.next);
    }

    // Insert the block into the free list in sorted order
    const index = this.#findBestFitIndex(block.size, DEFAULT_ALIGNMENT);
    this.#blocks.splice(index, 0, block);
    this.#freeSpace += block.size;
    block.isFree = true;
  }

  /**
   * Removes a memory block from the free list and marks it as allocated.
   *
   * This method updates the free space counter and removes the specified block
   * from the free list, marking it as no longer free.
   *
   * @param {MemoryBlock} block - The memory block to remove.
   */
  #removeBlock(block) {
    if (!block.isFree) return;

    let blockIndex = this.#blocks.indexOf(block);
    this.#freeSpace -= block.size;
    this.#blocks.splice(blockIndex, 1);
    block.isFree = false;
  }

  /**
   * Finds the best-fit free memory block for the requested size.
   *
   * This method searches the free list for the smallest block that is large enough
   * to satisfy the allocation request. Returns the block if found, or null if no suitable block exists.
   *
   * @param {number} size - The required size in bytes.
   * @returns {MemoryBlock|null} The best-fit memory block, or null if none is found.
   */
  #findBestFit(size, alignment) {
    let bestFitIndex = this.#findBestFitIndex(size, alignment);
    if (bestFitIndex >= this.#blocks.length) {
      return null;
    }
    return this.#blocks[bestFitIndex];
  }

  /**
   * Finds the index of the best-fit free memory block for the requested size.
   *
   * Uses binary search to locate the smallest block in the free list that is large enough
   * to satisfy the allocation request.
   *
   * @param {number} size - The required size in bytes.
   * @returns {number} The index of the best-fit block, or the length of the free list if none is found.
   */
  #findBestFitIndex(size, alignment) {
    let low = 0;
    let high = this.#blocks.length;

    while (low < high) {
      const mid = low + high >> 1;
      const block = this.#blocks[mid];
      let tooSmall = block.size < size;

      // Make sure the block is big enough if alignment is required.
      if (alignment !== DEFAULT_ALIGNMENT && !tooSmall) {
        const alignedOffset = alignForward(block.offset, alignment);
        const alignedSize = block.size - (alignedOffset - block.offset);
        tooSmall = alignedSize < size;
      }

      if (tooSmall) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }
    return low;
  }

  /**
   * Splits a block into two blocks.
   * The new block is returned, and the original block is modified.
   *
   * @param {MemoryBlock} block - The block to split.
   * @param {number} size - The size of the block after the split..
   * @returns {MemoryBlock} The new block.
   */
  #splitBlock(block, size) {
    const newBlock = new MemoryBlock(block.size - size, block.offset + size, block.isFree);
    newBlock.next = block.next;
    newBlock.prev = block;
    if (block.next) {
      block.next.prev = newBlock;
    }
    block.next = newBlock;
    block.size = size;
    return newBlock;
  }

  /**
   * Merges two adjacent memory blocks into one.
   * The left-hand block (lhs) absorbs the right-hand block (rhs).
   *
   * @param {MemoryBlock} lhs - The left-hand block that will absorb the right block
   * @param {MemoryBlock} rhs - The right-hand block that will be merged into the left block
   * @returns {MemoryBlock} The merged block (lhs)
   * @throws {Error} If blocks are not adjacent or in wrong order
   */
  #mergeAdjacentBlocks(lhs, rhs) {
    // Validate blocks are adjacent and in correct order
    if (!lhs || !rhs) {
      throw new Error('Cannot merge null blocks');
    }
    if (lhs.offset + lhs.size !== rhs.offset) {
      throw new Error('Blocks must be adjacent to merge');
    }

    // Perform the merge
    lhs.size += rhs.size;
    lhs.next = rhs.next;
    if (lhs.next) {
      lhs.next.prev = lhs;
    }

    // Clear references in the absorbed block to help GC
    rhs.next = null;
    rhs.prev = null;

    return lhs;
  }
}